import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Pencil, Trash2, Pause, Play, Home, MapPin, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useSubscription } from "../../hooks/useSubscription";
import { EmployerTabs } from "../../components/employer/EmployerTabs";
import { useConfirm } from "../../components/ConfirmProvider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";

const input = "w-full px-4 py-3 rounded-xl border border-stone-border focus:outline-none focus:ring-2 focus:ring-brand bg-white";
const empty = {
  restaurant_id: "", title: "", position_type: "", description: "", city: "", state: "", zip_code: "",
  contract_type: "full_time", salary_min: "", salary_max: "", schedule: "",
  requires_experience: false, requires_english: false, offers_housing: false, housing_details: "", benefits: "",
};

export default function EmployerJobs() {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { currentPlan } = useSubscription();
  const [jobs, setJobs] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/employer/jobs").then(({ data }) => setJobs(data || [])).catch(() => {}).finally(() => setLoading(false));
    api.get("/restaurants").then(({ data }) => setRestaurants(data || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const maxJobs = currentPlan?.max_jobs ?? 1;
  const activeCount = jobs.filter((j) => j.status === "active").length;
  const limitReached = maxJobs !== -1 && activeCount >= maxJobs;

  const openNew = () => {
    if (restaurants.length === 0) return toast.error(t("employer.jobs.needRestaurant"));
    if (limitReached) return toast.error(t("employer.jobs.limitReached", { max: maxJobs }));
    setEditing(null);
    setForm({ ...empty, restaurant_id: restaurants[0]?.id || "" });
    setOpen(true);
  };
  const openEdit = (j) => {
    setEditing(j);
    setForm({ ...empty, ...j, salary_min: j.salary_min ?? "", salary_max: j.salary_max ?? "", benefits: (j.benefits || []).join(", "), housing_details: j.housing_details || "", schedule: j.schedule || "", zip_code: j.zip_code || "" });
    setOpen(true);
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.checked }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      salary_min: form.salary_min === "" ? null : Number(form.salary_min),
      salary_max: form.salary_max === "" ? null : Number(form.salary_max),
      benefits: form.benefits.split(",").map((b) => b.trim()).filter(Boolean),
    };
    try {
      if (editing) await api.put(`/jobs/${editing.id}`, payload);
      else await api.post("/jobs", payload);
      toast.success(t("employer.jobs.saved"));
      setOpen(false);
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : t("common.error"));
    } finally { setSaving(false); }
  };

  const toggleStatus = async (j) => {
    try { await api.put(`/jobs/${j.id}/status`, { status: j.status === "active" ? "inactive" : "active" }); load(); }
    catch (err) { toast.error(t("common.error")); }
  };
  const remove = async (j) => {
    if (!(await confirm({ description: `${t("confirm.deleteDesc")}` }))) return;
    try { await api.delete(`/jobs/${j.id}`); toast.success(t("employer.jobs.deleted")); load(); }
    catch (err) { toast.error(t("common.error")); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="font-heading text-3xl font-bold text-stone-text mb-6">{t("employer.jobs.title")}</h1>
      <EmployerTabs />

      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <button data-testid="job-add-btn" onClick={openNew} className="flex items-center gap-2 px-5 py-3 rounded-xl bg-brand text-white font-semibold hover:bg-brand-hover transition-colors">
          <Plus className="w-4 h-4" /> {t("employer.jobs.add")}
        </button>
        <span className="text-sm text-stone-muted">{activeCount}{maxJobs === -1 ? "" : ` / ${maxJobs}`}</span>
      </div>

      {limitReached && (
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-3 mb-4 text-sm" data-testid="job-limit-warn">
          <Lock className="w-4 h-4" /> {t("employer.jobs.limitReached", { max: maxJobs })}
        </div>
      )}

      {loading ? (
        <div className="py-12 text-center text-stone-muted">{t("jobs.loading")}</div>
      ) : jobs.length === 0 ? (
        <div className="py-12 text-center text-stone-muted" data-testid="ejobs-empty">{t("employer.jobs.empty")}</div>
      ) : (
        <div className="space-y-3" data-testid="ejobs-list">
          {jobs.map((j) => (
            <div key={j.id} className="bg-white rounded-2xl border border-stone-border shadow-sm p-4 flex items-center justify-between gap-3" data-testid="ejob-item">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-heading font-semibold text-stone-text truncate">{j.title}</h3>
                  {j.offers_housing && <Home className="w-4 h-4 text-housing" />}
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${j.status === "active" ? "bg-housing/15 text-housing" : "bg-stone-bg text-stone-muted"}`}>
                    {j.status === "active" ? t("employer.jobs.active") : t("employer.jobs.inactive")}
                  </span>
                </div>
                <div className="flex items-center gap-1 text-xs text-stone-muted mt-1">
                  <MapPin className="w-3.5 h-3.5" /> {j.city}, {j.state} · {j.views || 0} {t("employer.jobs.views")}
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <button data-testid="job-status-btn" onClick={() => toggleStatus(j)} className="p-2 rounded-lg hover:bg-brand-soft text-stone-muted hover:text-brand">
                  {j.status === "active" ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                </button>
                <button data-testid="job-edit-btn" onClick={() => openEdit(j)} className="p-2 rounded-lg hover:bg-brand-soft text-stone-muted hover:text-brand"><Pencil className="w-4 h-4" /></button>
                <button data-testid="job-delete-btn" onClick={() => remove(j)} className="p-2 rounded-lg hover:bg-red-50 text-stone-muted hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="job-modal" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editing ? t("employer.jobs.edit") : t("employer.jobs.add")}</DialogTitle><DialogDescription>{t("employer.jobs.title")}</DialogDescription></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <select data-testid="job-restaurant" required className={input} value={form.restaurant_id} onChange={set("restaurant_id")}>
              {restaurants.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            <input data-testid="job-title" required placeholder={t("employer.jobs.form.title")} className={input} value={form.title} onChange={set("title")} />
            <input data-testid="job-position" required placeholder={t("employer.jobs.form.position")} className={input} value={form.position_type} onChange={set("position_type")} />
            <textarea data-testid="job-description" required rows={3} placeholder={t("employer.jobs.form.description")} className={input} value={form.description} onChange={set("description")} />
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="job-city" required placeholder={t("employer.jobs.form.city")} className={input} value={form.city} onChange={set("city")} />
              <input data-testid="job-state" required placeholder={t("employer.jobs.form.state")} className={input} value={form.state} onChange={set("state")} />
              <input data-testid="job-salary-min" type="number" placeholder={t("employer.jobs.form.salaryMin")} className={input} value={form.salary_min} onChange={set("salary_min")} />
              <input data-testid="job-salary-max" type="number" placeholder={t("employer.jobs.form.salaryMax")} className={input} value={form.salary_max} onChange={set("salary_max")} />
            </div>
            <select data-testid="job-contract" className={input} value={form.contract_type} onChange={set("contract_type")}>
              <option value="full_time">{t("employer.jobs.form.fullTime")}</option>
              <option value="part_time">{t("employer.jobs.form.partTime")}</option>
            </select>
            <input data-testid="job-schedule" placeholder={t("employer.jobs.form.schedule")} className={input} value={form.schedule} onChange={set("schedule")} />
            <input data-testid="job-benefits" placeholder={t("employer.jobs.form.benefits")} className={input} value={form.benefits} onChange={set("benefits")} />
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm text-stone-sub"><input type="checkbox" checked={form.requires_experience} onChange={toggle("requires_experience")} className="w-4 h-4 accent-[#D95A41]" /> {t("employer.jobs.form.requiresExp")}</label>
              <label className="flex items-center gap-2 text-sm text-stone-sub"><input type="checkbox" checked={form.requires_english} onChange={toggle("requires_english")} className="w-4 h-4 accent-[#D95A41]" /> {t("employer.jobs.form.requiresEnglish")}</label>
              <label className="flex items-center gap-2 text-sm text-stone-sub"><input data-testid="job-housing" type="checkbox" checked={form.offers_housing} onChange={toggle("offers_housing")} className="w-4 h-4 accent-[#22C55E]" /> {t("employer.jobs.form.offersHousing")}</label>
            </div>
            {form.offers_housing && (
              <textarea data-testid="job-housing-details" rows={2} placeholder={t("employer.jobs.form.housingDetails")} className={input} value={form.housing_details} onChange={set("housing_details")} />
            )}
            <DialogFooter>
              <button type="submit" data-testid="job-save" disabled={saving} className="px-5 py-2.5 rounded-xl bg-brand text-white font-semibold hover:bg-brand-hover disabled:opacity-60">{saving ? "..." : t("employer.jobs.form.save")}</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
